import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { pickLocalizedValue, useFilterLabels, useI18n } from "../i18n/I18nContext";
import { API_BASE, API_BASES, fetchApiJson } from "../config/apiBase";
import { lightenPropertyList } from "../Utils/propertyList";
import { formatPropertyPrice, getPropertyPriceSuffix } from "../Utils/propertyPricing";
import enMessages from "../i18n/locales/en.json";
import { ContactInfo } from "../Utils/Data";
import Loader from "../Components/Loader";
import { getHomeTestimonialsCopy } from "../content/localeCopy";
import { useTranslatedCopy } from "../i18n/useTranslatedCopy";
import Seo, { ARABIC_SEO_KEYWORDS, ENGLISH_SEO_KEYWORDS, mergeSeoKeywords } from "../Components/Seo";
import PropertyMapExplorer from "../Components/PropertyMapExplorer";

const fallbackPropertyImage = "/img/property-placeholder.svg";
const fallbackAgentAvatar = "/img/logo-black.png";
const homeStructuredData = {
  "@context": "https://schema.org",
  "@type": "RealEstateAgent",
  name: "QTECH",
  description:
    "Real estate services company in Saudi Arabia providing property management, brokerage, marketing, maintenance and operations, property development, and support for investment-focused real estate decisions.",
  telephone: ContactInfo.contact_phone,
  email: ContactInfo.contact_email,
  address: {
    "@type": "PostalAddress",
    streetAddress: ContactInfo.office_address_line1,
    addressLocality: "Al Khobar",
    addressRegion: "Eastern Province",
    postalCode: "34722",
    addressCountry: "SA",
  },
  areaServed: {
    "@type": "Country",
    name: "Saudi Arabia",
  },
  serviceType: [
    "Property Management",
    "Real Estate Brokerage",
    "Real Estate Marketing",
    "Property Maintenance and Operations",
    "Property Development",
    "Real Estate Investment Opportunities",
  ],
};
const homeServiceIcons = [
  "flaticon-house",
  "flaticon-mortgage",
  "flaticon-operator",
  "flaticon-house-1",
  "flaticon-building",
];
const homeServiceCardsFallback = [
  {
    title: "Property Management",
    body: "Comprehensive property management services focused on planning, supervision, maintenance, tenant coordination, rent collection, and clear reporting to protect asset value and support stable returns.",
    href: "/property-management-saudi-arabia",
    icon: "flaticon-house",
  },
  {
    title: "Real Estate Brokerage",
    body: "Real estate brokerage services connecting owners, buyers, and investors through market analysis, fair property pricing, professional negotiation, and compliant contract documentation.",
    href: "/real-estate-brokerage-saudi-arabia",
    icon: "flaticon-mortgage",
  },
  {
    title: "Real Estate Marketing",
    body: "Innovative real estate marketing solutions covering digital and traditional campaigns, professional property presentation, audience targeting, and stronger market visibility to maximize property value.",
    href: "/real-estate-marketing-saudi-arabia",
    icon: "flaticon-operator",
  },
  {
    title: "Maintenance & Operations",
    body: "Integrated maintenance and operations for properties and facilities, including preventive and corrective maintenance, rapid emergency response, and qualified technical teams to ensure asset sustainability.",
    href: "/property-maintenance-operations-saudi-arabia",
    icon: "flaticon-house-1",
  },
  {
    title: "Property Development",
    body: "End-to-end property development services from feasibility and design coordination to approvals, execution oversight, and handover, focused on quality delivery and long-term asset value.",
    href: "/property-development-saudi-arabia",
    icon: "flaticon-building",
  },
];
const heroSlidesFallback = Array.isArray(enMessages.home?.heroSlides)
  ? enMessages.home.heroSlides
  : [
      {
        bg: "/img/slider/0.jpg",
        innerClass: "text-center",
        title: "QTECH Real Estate Services",
        description:
          "Integrated real estate services focused on management, brokerage, marketing, maintenance, and development.",
      },
    ];

/* Prefer desktop hero art on mobile unless a slide explicitly sets mobileBg.
   Maintenance (333.jpg) and others stay on the same image as desktop. */
const heroDesktopMediaFocus = {
  "/img/slider/333.jpg": "18% center",
  "/img/slider/00.jpg": "22% center",
  "/img/service/gallery_7.jpg": "55% center",
  "/img/slider/new-slider.jpeg": "60% center",
  "/img/slider/14.jpg": "50% center",
};

const heroDesktopMediaFlip = {
  "/img/service/gallery_7.jpg": true,
};
const testimonialIconImage =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'%3E%3Crect width='160' height='160' rx='80' fill='%23f5f1df'/%3E%3Ccircle cx='80' cy='58' r='28' fill='%23bba55c'/%3E%3Cpath d='M32 132c8-28 25-42 48-42s40 14 48 42' fill='%23bba55c'/%3E%3C/svg%3E";
const resolveFeedbackImage = (value) => {
  if (!value) return testimonialIconImage;
  if (/^https?:\/\//i.test(value) || value.startsWith("data:")) return value;
  return value.startsWith("/") ? value : `/${value}`;
};
const testimonialsFallback = [
  {
    quote:
      "QTECH helped us take control of a mixed-use property with clear reporting, faster maintenance follow-up, and much better tenant coordination. Their property management team made the asset easier to run month after month.",
    name: "Muhammad Mustafa",
    role: "Property Owner",
    image: "img/testimonial/1.jpg",
  },
  {
    quote:
      "We used QTECH for real estate marketing on a residential project in Al Khobar. The listing presentation, campaign management, and buyer follow-up were professional, and the visibility of the project improved noticeably.",
    name: "Muhammad Mahmood",
    role: "Marketing Client",
    image: "img/testimonial/2.jpg",
  },
  {
    quote:
      "Their brokerage support was practical and transparent from pricing to negotiation. We felt supported through every step of the transaction, and the documentation process was handled with real care.",
    name: "Muhammad Ali",
    role: "Buyer Representative",
    image: "img/testimonial/3.jpg",
  },
  {
    quote:
      "QTECH supported our maintenance and operations needs with fast response times and organized technical follow-up. It gave us confidence that the property was being looked after properly on a daily basis.",
    name: "Huda Hoori",
    role: "Facility Supervisor",
    image: "img/testimonial/4.jpg",
  },
];
const testimonialsFallbackAr = [
  {
    quote:
      "ساعدتنا كيوتك على إدارة عقار متعدد الاستخدامات بشكل أفضل من خلال تقارير واضحة، ومتابعة أسرع لأعمال الصيانة، وتنسيق أكثر كفاءة مع المستأجرين. فريق إدارة الأملاك لديهم جعل تشغيل الأصل أسهل شهرًا بعد شهر.",
    name: "محمد مصطفى",
    role: "مالك عقار",
    image: "img/testimonial/1.jpg",
  },
  {
    quote:
      "تعاوننا مع كيوتك في التسويق العقاري لمشروع سكني في الخبر. كان عرض العقار وإدارة الحملات ومتابعة المشترين بمستوى احترافي، وتحسن حضور المشروع في السوق بشكل ملحوظ.",
    name: "محمد محمود",
    role: "عميل تسويق",
    image: "img/testimonial/2.jpg",
  },
  {
    quote:
      "دعمهم في الوساطة كان عمليًا وشفافًا من التسعير حتى التفاوض. شعرنا بالدعم في كل خطوة من خطوات الصفقة، وتم التعامل مع إجراءات التوثيق بعناية كبيرة.",
    name: "محمد علي",
    role: "ممثل مشتري",
    image: "img/testimonial/3.jpg",
  },
  {
    quote:
      "دعمتنا كيوتك في أعمال الصيانة والتشغيل بسرعة استجابة عالية ومتابعة فنية منظمة. هذا منحنا ثقة يومية بأن العقار تتم العناية به بالشكل الصحيح.",
    name: "هدى حوري",
    role: "مشرفة مرافق",
    image: "img/testimonial/4.jpg",
  },
];

const resolvePropertyImage = (url) => {
  if (!url) return fallbackPropertyImage;
  // Prefer placeholders over multi-MB inline base64 in list cards.
  if (/^data:image\//i.test(url)) {
    return url.length > 2048 ? fallbackPropertyImage : url;
  }
  if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/(uploads|api)\//i.test(url)) {
    return `${API_BASE}${new URL(url).pathname}`;
  }
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/uploads/") || url.startsWith("/api/")) return `${API_BASE}${url}`;
  return url.startsWith("/") ? url : `/${url}`;
};

const isSocialVideoUrl = (url) => /instagram\.com|tiktok\.com/i.test(String(url || ""));

const normalizeHeroSlide = (slide, fallbackSlide) => {
  const bg = slide?.bg || fallbackSlide?.bg || heroSlidesFallback[0].bg;
  const explicitMobileBg = slide?.mobileBg || fallbackSlide?.mobileBg || "";
  // Always use the desktop image unless a slide explicitly opts into a mobile asset.
  // This removes old mobile-only crops (e.g. maintenance) that didn't match desktop.
  const mobileBg = explicitMobileBg || bg;

  return {
    bg,
    mobileBg,
    mediaFocus: slide?.mediaFocus || fallbackSlide?.mediaFocus || heroDesktopMediaFocus[bg] || "center center",
    mediaFlip:
      typeof slide?.mediaFlip === "boolean"
        ? slide.mediaFlip
        : typeof fallbackSlide?.mediaFlip === "boolean"
          ? fallbackSlide.mediaFlip
          : Boolean(heroDesktopMediaFlip[bg]),
    innerClass: slide?.innerClass || fallbackSlide?.innerClass || "text-center",
    title: slide?.title || fallbackSlide?.title || "QTECH",
    headline: slide?.headline || fallbackSlide?.headline || slide?.title || fallbackSlide?.title || "QTECH",
    description:
      slide?.description ||
      fallbackSlide?.description ||
      "Integrated real estate services across management, brokerage, marketing, maintenance, and development.",
  };
};

const shortenHeroFeatureText = (value, maxWords = 10) => {
  const text = String(value || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  const words = text.split(" ");
  if (words.length <= maxWords) return text;
  return `${words.slice(0, maxWords).join(" ")}…`;
};

const renderAccentHeadline = (title) => {
  const words = String(title || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return null;
  if (words.length === 1) {
    return <span className="qtech-hero-accent">{words[0]}</span>;
  }
  const lead = words.slice(0, -1).join(" ");
  const accent = words[words.length - 1];
  return (
    <>
      {lead} <span className="qtech-hero-accent">{accent}</span>
    </>
  );
};

const getPropertyStatusMeta = (property, fl) => {
  const status = String(property?.status || "").toUpperCase();
  if (status === "SOLD") return { label: "Sold", className: "bg-danger" };
  if (status === "RENTED") return { label: "Rented", className: "bg-green" };
  const isRent = String(property?.listingType || "").toUpperCase() === "RENT";
  return { label: isRent ? fl.listing("Rent") : fl.listing("Sale"), className: isRent ? "bg-green" : "bg-orange" };
};

function waitForSlick(onReady, maxAttempts = 20, delayMs = 150) {
  let attempts = 0;
  let timerId = null;

  const check = () => {
    const $ = window.jQuery || window.$;
    if ($ && typeof $.fn?.slick === "function") {
      onReady($);
      return;
    }

    attempts += 1;
    if (attempts < maxAttempts) {
      timerId = window.setTimeout(check, delayMs);
    }
  };

  check();

  return () => {
    if (timerId) window.clearTimeout(timerId);
  };
}

function teardownSlick(root, namespace = "") {
  const $ = window.jQuery || window.$;
  if (!root || !$ || typeof $.fn?.slick !== "function") return;

  const $slider = $(root);
  if (namespace) {
    $slider.off(namespace);
  }

  if ($slider.hasClass("slick-initialized")) {
    try {
      $slider.slick("unslick");
    } catch {
      /* ignore stale slick instance */
    }
  }
}

const Home = () => {
  const { heroSlides, heroSubtitle, makeEnquiry, t, locale, isRtl, messages } = useI18n();
  const fl = useFilterLabels();
  const homeServiceCards = messages.home?.serviceCards || enMessages.home?.serviceCards || [];
  const localizedHeroSlides = useMemo(() => {
    const sourceSlides = Array.isArray(heroSlides) && heroSlides.length > 0 ? heroSlides : heroSlidesFallback;

    return sourceSlides.map((slide, index) =>
      normalizeHeroSlide(slide, heroSlidesFallback[index] || heroSlidesFallback[0])
    );
  }, [heroSlides]);
  const heroSliderKey = useMemo(
    () =>
      localizedHeroSlides
        .map(
          (slide, index) =>
            `${index}:${slide.bg}|${slide.mobileBg}|${slide.mediaFocus}|${slide.title}|${slide.headline}|${slide.description}`
        )
        .join("||"),
    [localizedHeroSlides]
  );
  const featuredSliderRef = useRef(null);
  const locationSliderRef = useRef(null);
  const heroSliderRef = useRef(null);
  const testimonialSliderRef = useRef(null);
  const [activeHeroIndex, setActiveHeroIndex] = useState(0);
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const propertiesLoadingLabel = locale === "ar" ? "جاري تحميل العقارات..." : "Properties are loading...";

  const heroFeatureItems = useMemo(() => {
    const cards = Array.isArray(homeServiceCards) && homeServiceCards.length > 0 ? homeServiceCards : homeServiceCardsFallback;
    return localizedHeroSlides.map((slide, index) => {
      const matchedCard =
        cards.find((card) => String(card?.title || "").toLowerCase() === String(slide.title || "").toLowerCase()) ||
        cards[index] ||
        homeServiceCardsFallback[index] ||
        null;
      return {
        label: slide.title,
        description: shortenHeroFeatureText(matchedCard?.body || slide.description, isRtl ? 8 : 9),
        href: matchedCard?.href || "/contact",
      };
    });
  }, [homeServiceCards, isRtl, localizedHeroSlides]);

  const goToHeroSlide = (index) => {
    const root = heroSliderRef.current;
    const $ = window.jQuery || window.$;
    setActiveHeroIndex(index);
    if (!root || !$ || typeof $.fn?.slick !== "function") return;
    const $slider = $(root);
    if ($slider.hasClass("slick-initialized")) {
      $slider.slick("slickGoTo", index);
    }
  };


useEffect(() => {
  let ignore = false;

  const fetchProperties = async () => {
    try {
      setPropertiesLoading(true);

      const { json, base } = await fetchApiJson(`/api/properties?lang=${locale}`);
      const list = lightenPropertyList(
        (Array.isArray(json) ? json : json.data || []).filter(
          (property) => property?.isActive !== false && String(property?.status || "").toUpperCase() !== "INACTIVE"
        ),
        base || API_BASE
      );

      if (!ignore) {
        setProperties(list);
      }

    } catch (err) {
      if (!ignore) {
        setProperties([]);
        window.__QTECH_PROPERTY_FETCH_ERROR__ =
          err?.message ||
          `Could not load properties from any configured API base: ${API_BASES.join(", ")}`;
        setPropertiesLoading(false);
      }
    } finally {
      if (!ignore) {
        setPropertiesLoading(false);
      }
    }
  };

  fetchProperties();

  return () => {
    ignore = true;
  };

}, [locale]);


  const propertiesByLocation = useMemo(() => {
    const grouped = new Map();

    properties.forEach((property) => {
      const city = (property.city || "").trim();
      const area = (property.area || "").trim();
      const location = (property.location || "").trim();
      const propertyTitle = (pickLocalizedValue(property, "title", locale, "") || "").trim();

      const keyBase = area || city || location;
      if (!keyBase) return;

      const key = keyBase.toLowerCase();

      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          title: propertyTitle || area || city || location,
          subtitle: area || city || location,
          locationLabel: area || city || location,
          searchValue: area || city || location,
          count: 0,
          image: null,
          propertyId: property.id || null,
          createdAt: property.createdAt || null,
        });
      }

      const group = grouped.get(key);
      group.count += 1;

      if (!group.image) {
        group.image = property.coverImage || property.image || null;
      }

      if (propertyTitle && (!group.title || group.title === group.locationLabel)) {
        group.title = propertyTitle;
      }

      if (
        property.createdAt &&
        (!group.createdAt || new Date(property.createdAt) > new Date(group.createdAt))
      ) {
        group.createdAt = property.createdAt;
        group.propertyId = property.id || group.propertyId;
      }
    });

    return Array.from(grouped.values())
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    })
    .slice(0, 6);
  }, [properties, locale]);

  const featuredProperties = useMemo(() => {
    return properties
      .filter(
        (property) =>
          property.isFeatured === true ||
          property.isFeatured === 1 ||
          property.isFeatured === "1" ||
          property.isFeatured === "true"
      )
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      .slice(0, 6);
  }, [properties]);
  const featuredSliderKey = useMemo(
    () => `${locale}:${featuredProperties.map((property) => property.id || property.slug || property.title).join("|")}`,
    [featuredProperties, locale]
  );
  const locationSliderKey = useMemo(
    () => `${locale}:${propertiesByLocation.map((group) => `${group.key}:${group.count}`).join("|")}`,
    [locale, propertiesByLocation]
  );

  useEffect(() => {
    const root = featuredSliderRef.current;
    if (!root || featuredProperties.length === 0) return undefined;
    let disposed = false;

    const cancelWait = waitForSlick(($) => {
      if (disposed || featuredSliderRef.current !== root || !root.isConnected) return;

      const $slider = $(root);
      teardownSlick(root);

      $slider.slick({
        accessibility: false,
        autoplay: true,
        autoplaySpeed: 3000,
        arrows: true,
        dots: false,
        infinite: true,
        rtl: isRtl,
        speed: 300,
        slidesToShow: 3,
        slidesToScroll: 1,
        prevArrow: '<a class="slick-prev"><i class="fas fa-arrow-left" alt="Arrow Icon"></i></a>',
        nextArrow: '<a class="slick-next"><i class="fas fa-arrow-right" alt="Arrow Icon"></i></a>',
        responsive: [
          {
            breakpoint: 1200,
            settings: {
              arrows: false,
              dots: true,
            },
          },
          {
            breakpoint: 992,
            settings: {
              arrows: false,
              dots: true,
              slidesToShow: 2,
              slidesToScroll: 1,
            },
          },
          {
            breakpoint: 768,
            settings: {
              arrows: false,
              dots: true,
              slidesToShow: 2,
              slidesToScroll: 1,
            },
          },
          {
            breakpoint: 580,
            settings: {
              arrows: false,
              dots: true,
              slidesToShow: 1,
              slidesToScroll: 1,
            },
          },
        ],
      });
    });

    return () => {
      disposed = true;
      cancelWait?.();
      teardownSlick(root);
    };
  }, [featuredProperties, featuredSliderKey, isRtl]);

  useEffect(() => {
    const root = locationSliderRef.current;
    if (!root || propertiesByLocation.length === 0) return undefined;
    let disposed = false;

    const cancelWait = waitForSlick(($) => {
      if (disposed || locationSliderRef.current !== root || !root.isConnected) return;

      const $slider = $(root);
      teardownSlick(root);

      $slider.slick({
        accessibility: false,
        autoplay: true,
        autoplaySpeed: 3000,
        arrows: true,
        dots: false,
        infinite: true,
        rtl: isRtl,
        speed: 300,
        slidesToShow: 3,
        slidesToScroll: 1,
        prevArrow: '<a class="slick-prev"><i class="fas fa-arrow-left" alt="Arrow Icon"></i></a>',
        nextArrow: '<a class="slick-next"><i class="fas fa-arrow-right" alt="Arrow Icon"></i></a>',
        responsive: [
          {
            breakpoint: 1200,
            settings: {
              arrows: false,
              dots: true,
            },
          },
          {
            breakpoint: 992,
            settings: {
              arrows: false,
              dots: true,
              slidesToShow: 2,
              slidesToScroll: 1,
            },
          },
          {
            breakpoint: 768,
            settings: {
              arrows: false,
              dots: true,
              slidesToShow: 2,
              slidesToScroll: 1,
            },
          },
          {
            breakpoint: 580,
            settings: {
              arrows: false,
              dots: true,
              slidesToShow: 1,
              slidesToScroll: 1,
            },
          },
        ],
      });
    });

    return () => {
      disposed = true;
      cancelWait?.();
      teardownSlick(root);
    };
  }, [isRtl, locationSliderKey, propertiesByLocation]);

  useEffect(() => {
    const root = heroSliderRef.current;
    if (!root || localizedHeroSlides.length === 0) return undefined;
    let disposed = false;

    const DEFAULT_AUTOPLAY_SPEED = 9000;
    const MAINTENANCE_AUTOPLAY_SPEED = 11000;
    const maintenanceSlideIndex = localizedHeroSlides.findIndex(
      (slide) => String(slide?.bg || "").includes("333.jpg")
    );

    const cancelWait = waitForSlick(($) => {
      if (disposed || heroSliderRef.current !== root || !root.isConnected) return;

      const $slider = $(root);

      const syncHeroSlideHeights = () => {
        const slides = root.querySelectorAll(".qtech-premium-hero-slide");
        if (!slides.length) return;

        let maxHeight = 0;
        slides.forEach((slide) => {
          slide.style.minHeight = "";
          const main = slide.querySelector(".qtech-premium-hero-main") || slide;
          const height = Math.max(main.scrollHeight || 0, main.offsetHeight || 0);
          if (height > maxHeight) maxHeight = height;
        });

        if (maxHeight > 0) {
          const nextHeight = `${Math.ceil(maxHeight)}px`;
          slides.forEach((slide) => {
            slide.style.minHeight = nextHeight;
          });
          const list = root.querySelector(".slick-list");
          if (list) list.style.height = nextHeight;
        }
      };

      const applyAutoplaySpeed = (slickInstance, slideIndex) => {
        if (!slickInstance) return;
        const nextSpeed =
          slideIndex === maintenanceSlideIndex
            ? MAINTENANCE_AUTOPLAY_SPEED
            : DEFAULT_AUTOPLAY_SPEED;

        slickInstance.options.autoplaySpeed = nextSpeed;
        if (slickInstance.autoPlayTimer) {
          clearInterval(slickInstance.autoPlayTimer);
          slickInstance.autoPlay();
        }
      };

      teardownSlick(root, ".qtechHeroTiming");

      $slider
        .off(".qtechHeroTiming")
        .on("init.qtechHeroTiming", (_event, slick) => {
          const current = slick?.currentSlide || 0;
          setActiveHeroIndex(current);
          applyAutoplaySpeed(slick, current);
          window.requestAnimationFrame(syncHeroSlideHeights);
        })
        .on("afterChange.qtechHeroTiming", (_event, slick, currentSlide) => {
          setActiveHeroIndex(currentSlide);
          applyAutoplaySpeed(slick, currentSlide);
          window.requestAnimationFrame(syncHeroSlideHeights);
        })
        .on("setPosition.qtechHeroTiming", () => {
          syncHeroSlideHeights();
        });

      $slider.slick({
        autoplay: true,
        autoplaySpeed: DEFAULT_AUTOPLAY_SPEED,
        arrows: true,
        dots: false,
        fade: true,
        cssEase: "cubic-bezier(0.4, 0, 0.2, 1)",
        infinite: true,
        rtl: isRtl,
        speed: 900,
        slidesToShow: 1,
        slidesToScroll: 1,
        waitForAnimate: true,
        pauseOnHover: true,
        pauseOnFocus: true,
        swipe: true,
        touchMove: true,
        touchThreshold: 12,
        prevArrow: '<a class="slick-prev"><i class="fas fa-arrow-left" alt="Arrow Icon"></i></a>',
        nextArrow: '<a class="slick-next"><i class="fas fa-arrow-right" alt="Arrow Icon"></i></a>',
        responsive: [
          {
            breakpoint: 992,
            settings: {
              arrows: false,
              dots: false,
              speed: 850,
              autoplaySpeed: 9000,
            },
          },
        ],
      });

      window.requestAnimationFrame(syncHeroSlideHeights);
      window.addEventListener("resize", syncHeroSlideHeights);
      root._qtechHeroSyncHeights = syncHeroSlideHeights;
    });

    return () => {
      disposed = true;
      cancelWait?.();
      if (root?._qtechHeroSyncHeights) {
        window.removeEventListener("resize", root._qtechHeroSyncHeights);
        delete root._qtechHeroSyncHeights;
      }
      teardownSlick(root, ".qtechHeroTiming");
    };
  }, [heroSliderKey, localizedHeroSlides, isRtl]);

  const localizedHomeServiceCards = useMemo(
    () =>
      (homeServiceCards || []).map((card, index) => ({
        ...card,
        icon: homeServiceIcons[index] || homeServiceCardsFallback[index]?.icon || "flaticon-house",
      })),
    [homeServiceCards]
  );
  const feedbackCopy = useTranslatedCopy(getHomeTestimonialsCopy(), locale, "home-testimonials-copy");
  const localizedTestimonials = useMemo(
    () =>
      (Array.isArray(feedbackCopy.testimonials) ? feedbackCopy.testimonials : []).map((item) => ({
        quote: String(item?.quote || "").trim(),
        name: String(item?.name || "").trim(),
        role: String(item?.role || "").trim(),
        image: resolveFeedbackImage(item?.image),
      })),
    [feedbackCopy.testimonials]
  );

  useEffect(() => {
    const root = testimonialSliderRef.current;
    if (!root || localizedTestimonials.length === 0) return undefined;
    let disposed = false;

    const cancelWait = waitForSlick(($) => {
      if (disposed || testimonialSliderRef.current !== root || !root.isConnected) return;

      const $slider = $(root);
      teardownSlick(root);

      $slider.slick({
        accessibility: false,
        autoplay: true,
        autoplaySpeed: 3500,
        arrows: true,
        dots: true,
        infinite: localizedTestimonials.length > 1,
        rtl: isRtl,
        speed: 300,
        slidesToShow: 1,
        slidesToScroll: 1,
        prevArrow: '<a class="slick-prev"><i class="fas fa-arrow-left" alt="Arrow Icon"></i></a>',
        nextArrow: '<a class="slick-next"><i class="fas fa-arrow-right" alt="Arrow Icon"></i></a>',
        responsive: [
          {
            breakpoint: 1200,
            settings: {
              arrows: false,
              dots: true,
            },
          },
          {
            breakpoint: 768,
            settings: {
              arrows: false,
              dots: true,
            },
          },
        ],
      });
    });

    return () => {
      disposed = true;
      cancelWait?.();
      teardownSlick(root);
    };
  }, [isRtl, localizedTestimonials]);

  return (
    <>
      <Seo
        title="QTECH | Real Estate Services in Saudi Arabia and Al Khobar"
        titleAr="شركة تقنيات الجودة | خدمات عقارية في الخبر والمنطقة الشرقية"
        description="QTECH is a real estate company in Saudi Arabia and Al Khobar offering property management, brokerage, real estate marketing, maintenance and operations, property development, and investment-focused property support."
        descriptionAr="شركة تقنيات الجودة تقدم خدمات عقارية في الخبر والمنطقة الشرقية تشمل الوساطة العقارية، إدارة الأملاك، التسويق العقاري، صيانة المباني، والتطوير العقاري."
        keywords={mergeSeoKeywords(
          ENGLISH_SEO_KEYWORDS.services,
          "real estate investment Saudi Arabia, property investment Saudi Arabia, real estate investment Al Khobar, Saudi property investment opportunities, investment properties Saudi Arabia, buy investment property Saudi Arabia, real estate investors Saudi Arabia"
        )}
        keywordsAr={mergeSeoKeywords(
          ARABIC_SEO_KEYWORDS.home,
          "استثمار عقاري, استثمار عقاري في السعودية, استثمار عقاري في الخبر, فرص استثمار عقاري, فرص عقارية استثمارية, عقارات استثمارية في السعودية, عقارات استثمارية في الخبر, شراء عقار استثماري, مستثمر عقاري"
        )}
        path="/"
        image="/img/slider/0.jpg"
        structuredData={homeStructuredData}
      />
      <div className="qtech-home-hero qtech-premium-hero">
        <div
          key={heroSliderKey}
          ref={heroSliderRef}
          className="qtech-hero-slider slick-slide-arrow-1"
        >
          {localizedHeroSlides.map((slide, index) => {
            const desktopBg = slide.bg;
            const mobileBg = slide.mobileBg || slide.bg;
            const learnMoreHref = heroFeatureItems[index]?.href || "/services";
            return (
              <div
                key={`hero-slide-${index}`}
                className="qtech-premium-hero-slide"
                style={{
                  "--qtech-hero-desktop-bg": `url("${desktopBg}")`,
                  "--qtech-hero-mobile-bg": `url("${mobileBg}")`,
                  "--qtech-hero-media-focus": slide.mediaFocus || "center center",
                }}
              >
                <div className="qtech-premium-hero-main">
                  <div className="qtech-premium-hero-copy">
                    <p className="qtech-premium-hero-eyebrow">{slide.title}</p>
                    <h1 className="qtech-premium-hero-title">{renderAccentHeadline(slide.headline)}</h1>
                    <p className="qtech-premium-hero-brief">{slide.description}</p>
                    <div className="qtech-premium-hero-actions">
                      <Link to={learnMoreHref} className="qtech-premium-hero-cta">
                        {makeEnquiry}
                      </Link>
                    </div>
                  </div>
                  <div
                    className={`qtech-premium-hero-media${
                      Boolean(slide.mediaFlip) !== Boolean(isRtl) ? " qtech-premium-hero-media--flip" : ""
                    }`}
                    role="img"
                    aria-label={slide.title}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="qtech-hero-features" aria-label={heroSubtitle}>
          {heroFeatureItems.map((feature, index) => (
            <button
              key={`hero-feature-${feature.label}-${index}`}
              type="button"
              className={`qtech-hero-feature${activeHeroIndex === index ? " is-active" : ""}`}
              onClick={() => goToHeroSlide(index)}
            >
              <span className="qtech-hero-feature-label">{feature.label}</span>
              <span className="qtech-hero-feature-text">{feature.description}</span>
            </button>
          ))}
        </div>
      </div>

      <PropertyMapExplorer
        properties={properties}
        loading={propertiesLoading}
        locale={locale}
        isRtl={isRtl}
        resolveImage={resolvePropertyImage}
        labels={{
          eyebrow: locale === "ar" ? "خريطة العقارات" : "Property Map",
          title: locale === "ar" ? "عقاراتنا في المنطقة الشرقية" : "Our properties in Eastern Province",
          subtitle:
            locale === "ar"
              ? "صفِّ حسب المدينة، اضغط على النقطة المضيئة، وافتح تفاصيل العقار مباشرة."
              : "Filter by city, tap a glowing pin, and open the property details.",
          all: locale === "ar" ? "الكل" : "All",
          empty: locale === "ar" ? "لا توجد عقارات لهذه المدينة على الخريطة حالياً." : "No properties on the map for this city yet.",
          loading: locale === "ar" ? "جاري تحميل الخريطة..." : "Loading map...",
          viewProperty: locale === "ar" ? "عرض العقار" : "View property",
          approx: locale === "ar" ? "موقع تقريبي حسب المدينة" : "Approx. city location",
          countLabel: locale === "ar" ? "عقارات ظاهرة" : "properties shown",
        }}
      />

      <div className="ltn__service-area section-bg-1 pt-115 pb-70">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="section-title-area ltn__section-title-2--- text-center">
                <h6 className="section-subtitle section-subtitle-2 ltn__secondary-color">
                  {t("servicesPage.coreSubtitle")}
                </h6>
                <h1 className="section-title">{t("servicesPage.coreTitle")}</h1>
              </div>
            </div>
          </div>
          <div className="row justify-content-center gy-4">
            {localizedHomeServiceCards.map((card) => (
              <div className="col-lg-4 col-sm-6 col-12 d-flex" key={card.href}>
                <div className="ltn__feature-item ltn__feature-item-6 text-center bg-white box-shadow-1 h-100 w-100 d-flex flex-column">
                  <div className="ltn__feature-icon">
                    <span>
                      <i className={card.icon} />
                    </span>
                  </div>
                  <div className="ltn__feature-info">
                    <h3 className="animated fadeIn">
                      <Link to={card.href}>{card.title}</Link>
                    </h3>
                    <p>{card.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>





{/*============== Properties By Location =================*/}



    <div
  className="ltn__search-by-place-area section-bg-1 before-bg-top--- bg-image-top--- pt-115 pb-70"
  data-bs-bg="img/bg/20.jpg"
>
  <div className="container">
    <div className="row">
      <div className="col-lg-12">
        <div className="section-title-area ltn__section-title-2--- text-center">
          <h6 className="section-subtitle section-subtitle-2 ltn__secondary-color">
            {t("home.areaPropertiesSubtitle")}
          </h6>
          <h1 className="section-title">{t("home.propertiesByLocationTitle")}</h1>
        </div>
      </div>
    </div>

    {propertiesLoading ? (
      <div className="row">
        <div className="col-12">
          <Loader label={propertiesLoadingLabel} />
        </div>
      </div>
    ) : (
      <div key={locationSliderKey} ref={locationSliderRef} className="row qtech-location-slider slick-arrow-1">
        {propertiesByLocation.length === 0 ? (
          <div className="col-12 text-center">
            <p>{t("home.propertiesByLocationEmpty")}</p>
          </div>
        ) : null}

        {propertiesByLocation.map((group) => (
          <div className="col-lg-4 col-md-6 col-12" key={group.key}>
            <div className="ltn__search-by-place-item">
              <div className="search-by-place-img">
                <Link to={`/property?area=${encodeURIComponent(group.searchValue)}`}>
                  <img
                    src={resolvePropertyImage(group.image)}
                    alt={group.title}
                  />
                </Link>

                <div className="search-by-place-badge">
                  <ul>
                    <li>{`${group.count} ${t("pages.properties.title")}`}</li>
                  </ul>
                </div>
              </div>

              <div className="search-by-place-info">
                <h6>
                  <Link to={`/property?area=${encodeURIComponent(group.searchValue)}`}>
                    {group.locationLabel}
                  </Link>
                </h6>

                <h4>
                  <Link to={`/property?area=${encodeURIComponent(group.searchValue)}`}>
                    {group.title}
                  </Link>
                </h4>

                <div className="search-by-place-btn">
                  <Link to={`/property?area=${encodeURIComponent(group.searchValue)}`}>
                    {t("home.viewProperties")} <i className="flaticon-right-arrow" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
</div>





<div className="ltn__product-slider-area ltn__product-gutter pt-115 pb-90">
  <div className="container">
    <div className="row">
      <div className="col-lg-12">
        <div className="section-title-area ltn__section-title-2--- text-center">
          <h6 className="section-subtitle section-subtitle-2 ltn__secondary-color">
            {t("home.featuredListingsSubtitle")}
          </h6>
          <h1 className="section-title">{t("home.featuredListingsTitle")}</h1>
        </div>
      </div>
    </div>

    {propertiesLoading ? (
      <div className="row">
        <div className="col-12">
          <Loader label={propertiesLoadingLabel} />
        </div>
      </div>
    ) : featuredProperties.length === 0 ? (
      <div className="row">
        <div className="col-12 text-center">
          <p>{t("home.featuredListingsEmpty")}</p>
        </div>
      </div>
    ) : (
      <div key={featuredSliderKey} ref={featuredSliderRef} className="row qtech-featured-slider slick-arrow-1">
        {featuredProperties.map((property) => {
          const statusMeta = getPropertyStatusMeta(property, fl);
          const image = property.coverImage || property.image || fallbackPropertyImage;
          const location =
            [pickLocalizedValue(property, "area", locale, ""), pickLocalizedValue(property, "city", locale, "")]
              .filter(Boolean)
              .join(", ") ||
            property.location ||
            t("property.locationNotSet");
          const title = pickLocalizedValue(property, "title", locale, "Featured Property");
          const descriptionSource =
            pickLocalizedValue(property, "shortDescription", locale, "") ||
            pickLocalizedValue(property, "description", locale, "");
          const imageCount =
            property.imageCount ??
            (Array.isArray(property.images) ? property.images.length : 0);
          const mediaCount = [property.videoUrl, property.floorPlanImageUrl1, property.floorPlanImageUrl2].filter(Boolean)
            .length;
          const description =
            descriptionSource.length > 125
              ? `${descriptionSource.slice(0, 125).trimEnd()}...`
              : descriptionSource || "Discover this featured property.";
          const rawAgentName = property.createdBy?.fullName || "";
          const agentName = /^qtech\s+agent$/i.test(rawAgentName.trim()) ? "QTECH Admin" : rawAgentName || "QTECH Admin";
          const agentRole = property.createdBy?.agentProfile?.companyName || "Property Consultant";
          const agentAvatar =
            property.createdBy?.agentProfile?.profileImageUrl ||
            property.createdBy?.profileImageUrl ||
            property.agentImage ||
            fallbackAgentAvatar;
          const priceInfo = formatPropertyPrice(property, {
            negotiationText: t("property.clickForNegotiation"),
          });

          return (
            <div className="col-lg-4 col-md-6 col-12" key={`featured-${property.id}`}>
              <div className="ltn__product-item ltn__product-item-4 text-center---">
                <div className="product-img">
                  <Link to={`/property/${property.id}`}>
                    <img className="qtech-property-card-image" src={image} alt={title || "Property"} />
                  </Link>
                  <div className="product-badge">
                    <ul>
                      <li className={`sale-badge ${statusMeta.className}`}>
                        {statusMeta.label}
                      </li>
                    </ul>
                  </div>
                  <div className="product-img-location-gallery">
                    <div className="product-img-location">
                      <ul>
                        <li>
                          <span>
                            <i className="flaticon-pin" /> {location}
                          </span>
                        </li>
                      </ul> 
                    </div>
                    <div className="product-img-gallery">
                      <ul>
                        <li>
                          <span>
                            <i className="fas fa-camera" /> {imageCount}
                          </span>
                        </li>
                        <li>
                          <span>
                            <i className="fas fa-film" /> {mediaCount}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="product-info">
                  <div className="product-price">
                    {priceInfo.isNegotiation ? (
                      <a href={priceInfo.href} target="_blank" rel="noreferrer">
                        {priceInfo.text}
                      </a>
                    ) : (
                      <span>
                        {priceInfo.text || "—"}
                        {priceInfo.text ? (
                          <label>{` ${getPropertyPriceSuffix(property, t, priceInfo.suffix)}`}</label>
                        ) : null}
                      </span>
                    )}
                  </div>
                  <h2 className="product-title">
                    <Link to={`/property/${property.id}`}>{title}</Link>
                  </h2>
                  <div className="product-description">
                    <p>{description || " "}</p>
                  </div>
                  <ul className="ltn__list-item-2 ltn__list-item-2-before">
                    <li>
                      <span>
                        {property.bedrooms ?? 0} <i className="flaticon-bed" />
                      </span>
                      {t("property.bedrooms")}
                    </li>
                    <li>
                      <span>
                        {property.bathrooms ?? 0} <i className="flaticon-clean" />
                      </span>
                      {t("property.bathrooms")}
                    </li>
                    <li>
                      <span>
                        {property.areaSqft ?? property.area ?? 0}{" "}
                        <i className="flaticon-square-shape-design-interface-tool-symbol" />
                      </span>
                      {t("property.squareFt")}
                    </li>
                  </ul>
                </div>

                <div className="product-info-bottom">
                  <div className="real-estate-agent">
                    <div className="agent-img">
                      <Link to={`/property/${property.id}`}>
                        <img
                          src={resolvePropertyImage(agentAvatar)}
                          alt={agentName}
                          className={agentAvatar === fallbackAgentAvatar ? "qtech-agent-logo-avatar" : ""}
                          onError={(event) => {
                            event.currentTarget.src = fallbackAgentAvatar;
                            event.currentTarget.classList.add("qtech-agent-logo-avatar");
                          }}
                        />
                      </Link>
                    </div>
                    <div className="agent-brief">
                      <h6>
                        <Link to={`/property/${property.id}`}>{agentName}</Link>
                      </h6>
                      <small>{agentRole}</small>
                    </div>
                  </div>
                  <div className="product-hover-action">
                    <ul>
                      <li>
                        <Link to={`/property/${property.id}`} title="Quick View">
                          <i className="flaticon-expand" />
                        </Link>
                      </li>
                      <li>
                        <a
                          href="#"
                          title="Wishlist"
                          data-bs-toggle="modal"
                          data-bs-target="#liton_wishlist_modal"
                        >
                          <i className="flaticon-heart-1" />
                        </a>
                      </li>
                      <li>
                        <Link to={`/property/${property.id}`} title="Product Details">
                          <i className="flaticon-add" />
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
</div>

<div className="ltn__video-popup-area ltn__video-popup-margin---">
  <div
    className="ltn__video-bg-img ltn__video-popup-height-600--- bg-overlay-black-30 bg-image bg-fixed ltn__animation-pulse1"
    data-bs-bg="img/bg/19.jpg"
    style={{ backgroundImage: 'url("img/bg/19.jpg")' }}
  >
    <a
      className="ltn__video-icon-2 ltn__video-icon-2-border---"
      href="https://www.instagram.com/reel/DXoyBdhJNHo/"
      {...(isSocialVideoUrl("https://www.instagram.com/reel/DXoyBdhJNHo/")
        ? { target: "_blank", rel: "noopener noreferrer" }
        : { "data-rel": "lightcase:myCollection" })}
    >
      <i className="fa fa-play" />
    </a>
  </div>
</div>



      <div className="ltn__testimonial-area ltn__testimonial-4 pt-115 pb-100 plr--9">
        <div className="container-fluid">
    <div className="row">
      <div className="col-lg-12">
        <div className="section-title-area ltn__section-title-2--- text-center">
            <h6 className="section-subtitle section-subtitle-2 ltn__secondary-color">
              {feedbackCopy.clientsFeedbackSubtitle || t("home.clientsFeedbackSubtitle")}
            </h6>
            <h1 className="section-title">{feedbackCopy.clientsFeedbackTitle || t("home.clientsFeedbackTitle")}</h1>
          </div>
        </div>
      </div>
        <div className="row">
          <div className="col-lg-12">
            <div
              ref={testimonialSliderRef}
              className="ltn__testimonial-slider-4 qtech-home-testimonial-slider slick-arrow-1"
            >
              {localizedTestimonials.map((item, index) => (
                <div className="ltn__testimonial-item-5" key={`testimonial-${index}`}>
                <div className="ltn__quote-icon">
                  <i className="far fa-comments" />
                </div>
                <div className="ltn__testimonial-image">
                  <img src={item.image || testimonialIconImage} alt={item.name || "Client"} />
                </div>
                <div className="ltn__testimonial-info">
                  <p>{item.quote}</p>
                  <h4>{item.name}</h4>
                <h6>{item.role}</h6>
              </div>
            </div>
          ))}
        </div>
        <ul className="ltn__testimonial-quote-menu d-none d-lg-block">
          <li>
            <img src={localizedTestimonials[0]?.image || testimonialIconImage} alt="Client" />
          </li>
          <li>
            <img src={localizedTestimonials[1]?.image || localizedTestimonials[0]?.image || testimonialIconImage} alt="Client" />
          </li>
          <li>
            <img src={localizedTestimonials[2]?.image || localizedTestimonials[0]?.image || testimonialIconImage} alt="Client" />
          </li>
          <li>
            <img src={localizedTestimonials[3]?.image || localizedTestimonials[0]?.image || testimonialIconImage} alt="Client" />
          </li>
        </ul>
      </div>
    </div>
  </div>
</div>

    </>
  );
};

export default Home;
